import { useMutation, useQueryClient } from 'react-query';

import { MutationFunction, useRequest } from '@frontend/api';

import type {
  SavedView,
  SavedViewFilter,
  SavedViewUpdatePayload,
} from './types';
import {
  getSavedViewsQueryKey,
  getSavedViewDetailsQueryKey,
  getUpdateSavedViewMutationKey,
} from './utils';

export const useUpdateSavedView: MutationFunction<
  SavedView,
  SavedViewUpdatePayload
> = ({ query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);
  const queryClient = useQueryClient();
  const listKey = getSavedViewsQueryKey();

  return useMutation(
    (requestData) =>
      request<SavedView>(`/display_configs/views/${requestData.viewId}`, {
        query,
        init: {
          method: 'PATCH',
          body: requestData,
        },
      }),
    {
      ...queryOptions,
      onMutate: async (savedViewPayload) => {
        queryOptions?.onMutate?.(savedViewPayload);
        const detailsQueryKey = getSavedViewDetailsQueryKey(savedViewPayload);

        await queryClient.cancelQueries(listKey);
        await queryClient.cancelQueries({ queryKey: detailsQueryKey });

        const previousViews = queryClient.getQueryData(listKey);
        const previousView =
          queryClient.getQueryData<SavedView>(detailsQueryKey);
        const newFilters: SavedViewFilter[] =
          savedViewPayload.filters?.map((filter) => ({
            ...filter,
            queryFormattedKey: `${filter.queryParamName}_${filter.filterCondition}`,
            queryFormattedValue: filter.value.toString(),
            viewId: savedViewPayload.viewId,
          })) ?? ([] as SavedViewFilter[]);
        // Optimistically update to the new value
        queryClient.setQueryData(
          detailsQueryKey,
          (old?: SavedView) =>
            ({ ...old, ...savedViewPayload, filters: newFilters } as SavedView),
        );
        queryClient.setQueryData(
          listKey,
          (old?: SavedView[]) =>
            old?.map((v) =>
              v.id === savedViewPayload.viewId
                ? ({
                    ...v,
                    ...savedViewPayload,
                    filters: newFilters,
                  } as SavedView)
                : v,
            ) ?? [],
        );

        // Return a context with the previous state
        return { previousViews, previousView };
      },
      onSuccess: (...args) => {
        queryOptions?.onSuccess?.(...args);

        const [savedView, savedViewPayload] = args;
        const detailsQueryKey = getSavedViewDetailsQueryKey(savedViewPayload);
        queryClient.setQueryData(detailsQueryKey, savedView);
        queryClient.setQueryData(
          listKey,
          (old?: SavedView[]) =>
            old?.map((v) => (v.id === savedView.id ? savedView : v)) ?? [],
        );
      },
      // If the mutation fails, use the context we returned above
      onError: (...args) => {
        queryOptions?.onError?.(...args);

        const [, savedViewPayload, context] = args;
        queryClient.setQueryData(
          getSavedViewDetailsQueryKey(savedViewPayload),
          context!.previousView,
        );
        queryClient.setQueryData(listKey, context!.previousViews);
      },
      // Always refetch after error or success:
      onSettled: (...args) => {
        queryOptions?.onSettled?.(...args);

        const [, , savedViewPayload] = args;
        queryClient.invalidateQueries({
          queryKey: getSavedViewDetailsQueryKey(savedViewPayload),
        });
        queryClient.invalidateQueries({ queryKey: listKey });
      },
      mutationKey: getUpdateSavedViewMutationKey(),
    },
  );
};
